/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import GeoMap from './GeoMap';
import type { ClusterPick } from './GeoMap';
import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFingerprintsGeo, getFingerprintsGeoLeaves, GeoCommand, GeoPoint,
         GeoLeavesResult } from '../data';
import GeoMapFingerDialog from './GeoMapFingerDialog';
import { StatusContext, StatusContextType } from './StatusContext';

const PAGE_SIZE = 100;

const defaultFilter: GeoCommand = {
    cmd: '',
    businessLocations: true,
    ipGeolocations: true,
    filter: {
        search: '',
        allLayers: true,
        layer: 0,
        selected: 'all',
        tag: '',
        tagValue: '',
    }
};

// 'add' stacks another query onto the map, so coordinates that both queries
// returned have to be merged rather than drawn twice
function mergePoints(prev: GeoPoint[], next: GeoPoint[]): GeoPoint[] {
    const byKey = new Map<string, GeoPoint>();
    for (const p of [ ...prev, ...next ]) {
        const key = `${p.lat},${p.lon},${p.type}`;
        const seen = byKey.get(key);
        if (seen) {
            seen.n = Math.max(seen.n, p.n);
            seen.nf = Math.max(seen.nf, p.nf);
        } else {
            byKey.set(key, { ...p });
        }
    }
    return [ ...byKey.values() ];
}

type GeoRequest = {
    seq: number;
    mode: string;   // 'set' replaces what is on the map, 'add' merges into it
    args: {
        businessLocations: boolean;
        ipGeolocations: boolean;
        filter: GeoCommand['filter'];
    };
};

function GeoPage({ }) {
    const [ annos, setAnnos ] = useState<GeoPoint[]>([]);
    const [ request, setRequest ] = useState<GeoRequest | null>(null);
    const seq = useRef(0);
    const [ showFingerDialog, setShowFingerDialog ] = useState(false);
    const [ geoCommand, setGeoCommand ] = useState<GeoCommand>(defaultFilter);
    const [ loading, setLoading ] = useState(false);
    const [ pick, setPick ] = useState<ClusterPick | null>(null);
    const [ leaves, setLeaves ] = useState<GeoLeavesResult | null>(null);
    const [ leavesLoading, setLeavesLoading ] = useState(false);
    const [ offset, setOffset ] = useState(0);
    // The filter the points on screen were fetched with, so drilling into a
    // cluster asks the server the same question the map is showing
    const [ activeCommand, setActiveCommand ] = useState<GeoCommand>(defaultFilter);
    const statusContext = useContext(StatusContext) as StatusContextType;
    const navigate = useNavigate();

    const handleClear = () => {
        setAnnos([]);
        setPick(null);
        setLeaves(null);
    };

    /*
     * The dialog and the Finger button both work by setting cmd on the form
     * state.  That gets turned into a numbered request here and the flag is
     * cleared immediately, which keeps the fetch below keyed on something that
     * only changes when a genuinely new query is asked for.  Clearing the flag
     * inside the fetching effect instead would re-run it and have its own
     * cleanup cancel the request it had just issued.
     */
    useEffect(() => {
        if (!geoCommand.cmd) {
            return;
        }
        const mode = geoCommand.cmd;
        seq.current += 1;
        setRequest({
            seq: seq.current,
            mode,
            args: {
                businessLocations: geoCommand.businessLocations,
                ipGeolocations: geoCommand.ipGeolocations,
                filter: geoCommand.filter
            }
        });
        setGeoCommand((prev) => ({ ...prev, cmd: '' }));
    }, [ geoCommand ]);

    useEffect(() => {
        if (!request) {
            return;
        }
        let cancelled = false;
        async function fetchData() {
            setLoading(true);
            try {
                const resp = await getFingerprintsGeo(request!.args);
                if (cancelled) {
                    return;
                }
                // Functional update, so this effect never has to depend on the
                // very state it writes
                setAnnos((prev) => (request!.mode === 'add'
                    ? mergePoints(prev, resp || [])
                    : (resp || [])));
                setActiveCommand({ ...request!.args, cmd: '' });
                setPick(null);
                setLeaves(null);
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching geo fingerprints:', error);
                    const message = (error as any)?.message || String(error);
                    statusContext.postError(`Geo lookup failed: ${message}. Configure IP geolocation credentials in Settings -> Identity.`);
                }
            }
            if (!cancelled) {
                setLoading(false);
            }
        }
        fetchData();
        return () => { cancelled = true; };
    }, [ request, statusContext ]);

    // Drill down for whatever cluster or point is currently selected
    useEffect(() => {
        if (!pick) {
            return;
        }
        let cancelled = false;
        async function fetchLeaves() {
            setLeavesLoading(true);
            try {
                const resp = await getFingerprintsGeoLeaves({
                    bbox: pick!.bbox,
                    businessLocations: activeCommand.businessLocations,
                    ipGeolocations: activeCommand.ipGeolocations,
                    filter: activeCommand.filter,
                    limit: PAGE_SIZE,
                    offset
                });
                if (!cancelled) {
                    setLeaves(resp);
                }
            } catch (error) {
                if (!cancelled) {
                    const message = (error as any)?.message || String(error);
                    statusContext.postError(`Geo detail failed: ${message}`);
                }
            }
            if (!cancelled) {
                setLeavesLoading(false);
            }
        }
        fetchLeaves();
        return () => { cancelled = true; };
    }, [ pick, offset, activeCommand, statusContext ]);

    const handlePick = useCallback((next: ClusterPick) => {
        // A coordinate holding exactly one fingerprint goes straight to it
        if (next.fid !== undefined) {
            navigate(`/fingerprint/${next.fid}`);
            return;
        }
        setOffset(0);
        setPick(next);
    }, [ navigate ]);

    const totalObservations = annos.reduce((a, p) => a + (p.n || 0), 0);
    const pageCount = leaves ? Math.ceil(leaves.total / PAGE_SIZE) : 0;
    const pageNum = Math.floor(offset / PAGE_SIZE) + 1;

    return (
        <div style={{ position: 'relative', height: 'calc(100vh - 5rem)', minHeight: '30rem' }}>
            <div
                style={{
                    position: 'absolute',
                    top: 8,
                    left: 16,
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    zIndex: 10,
                    borderRadius: 6,
                    padding: 6,
                }}
            >
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleClear}
                >
                    Clear
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={ () => setShowFingerDialog(true) }
                >
                    Advanced
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={loading}
                    onClick={ () => {
                        setGeoCommand({ ...defaultFilter, cmd: 'set' });
                    }}
                >
                    { loading ? 'Loading...' : 'Finger' }
                </button>
                { annos.length > 0 ? (
                    <span className="badge bg-dark border border-secondary ms-2">
                        { totalObservations.toLocaleString() } points at { annos.length.toLocaleString() } locations
                    </span>
                ) : null }
            </div>

            <GeoMap points={annos} onPick={handlePick} />

            { pick ? (
                <div
                    className="border border-secondary bg-dark text-light"
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bottom: 8,
                        width: '26rem',
                        maxWidth: '45vw',
                        zIndex: 20,
                        borderRadius: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <div className="d-flex justify-content-between align-items-start p-2 border-bottom border-secondary">
                        <div>
                            <div className="fw-bold">
                                { leaves ? leaves.total.toLocaleString() : '--' } fingerprints
                            </div>
                            {/*
                              * The point count comes from the server once it
                              * answers, so it describes the same box as the
                              * fingerprint total above it.  A cluster's own
                              * sum covers only its members, while the box
                              * drawn around them can catch a few more.
                              */}
                            <div className="small text-muted">
                                { (leaves?.observations ?? pick.observations).toLocaleString() } points
                                { pick.nip ? ` · ${pick.nip.toLocaleString()} ip` : '' }
                                { pick.nbusiness ? ` · ${pick.nbusiness.toLocaleString()} business` : '' }
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={() => { setPick(null); setLeaves(null); }}
                        />
                    </div>
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        { leavesLoading && !leaves ? (
                            <div className="p-3 text-muted small">Loading...</div>
                        ) : null }
                        { leaves && leaves.rows.length === 0 ? (
                            <div className="p-3 text-muted small">No fingerprints here.</div>
                        ) : null }
                        <table className="table table-sm table-dark table-hover mb-0">
                            <tbody>
                                { (leaves?.rows || []).map((row) => (
                                    <tr
                                        key={row.fid}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/fingerprint/${row.fid}`)}
                                    >
                                        <td>
                                            <div className="text-truncate" style={{ maxWidth: '17rem' }}>
                                                { row.vendor } { row.product }
                                            </div>
                                            <div className="small text-muted text-truncate"
                                                 style={{ maxWidth: '17rem' }}>
                                                { row.pvkey }
                                            </div>
                                        </td>
                                        <td className="text-end align-middle small text-muted">
                                            { row.n }
                                        </td>
                                    </tr>
                                )) }
                            </tbody>
                        </table>
                    </div>
                    { pageCount > 1 ? (
                        <div className="d-flex justify-content-between align-items-center p-2 border-top border-secondary">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={offset === 0 || leavesLoading}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                            >
                                Prev
                            </button>
                            <span className="small text-muted">
                                { pageNum } / { pageCount }
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={pageNum >= pageCount || leavesLoading}
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                            >
                                Next
                            </button>
                        </div>
                    ) : null }
                </div>
            ) : null }

            <GeoMapFingerDialog show={showFingerDialog} setShow={setShowFingerDialog}
                                geoCommand={geoCommand} setGeoCommand={setGeoCommand}/>
        </div>
    );
}

export default GeoPage;
